import { Reducer } from 'redux';
import { cloneDeep, isNil } from 'lodash-es';
import dayjs from 'dayjs';
import { PreviewPageType, FormTypeEnum, FormPayload, FormSettingsEnum, LandingProductCurrencyEnum } from '@dev/ee-api/ee-api-models';
import { EditorTemplateStatus } from '@dev/template-editor';
import { CONFIRMATION_MULTI_TEMPLATE_INDEX, PAYMENT_MULTI_TEMPLATE_INDEX, PRODUCT_MULTI_TEMPLATE_INDEX } from '@/components/CheckoutCreator/constants';
import {
    CheckoutCreatorState,
    TemplateUploadFileSuccessAction,
    CheckoutCreatorActionTypes,
    SetIsTemplateOpenAction,
    OpenUpsellingOverlayAction,
    ModifyCheckoutAction,
    CheckoutCreatorStatus,
    LoadSuccessAction,
    SaveSuccessAction,
    SetTemplatePreviewAction,
    CheckoutCreatorTemplate,
    StartEditStepAction,
    SetCheckoutErrorsAction,
    TemplateFormAddNewAction,
    TemplateFormUpdateSavedAction,
    TemplateFormAddLoadedAction,
    TemplateFormsRemoveAction,
    TemplateFormDuplicatedAction,
    TemplateFormPayloadUpdateAction,
    TemplateFormSetMailingPreferencesLabelAction,
    TemplateFormsSetInvalidAction,
    TemplateFormSetErrorsAction,
    ModifyCheckoutMetaDataAction,
    CheckoutMetaData,
    TemplateFormsSetDefaultDoubleOptInDisabledAction,
    LoadPageTemplateSuccessAction,
    AddContactListErrorStatusAction,
    SetUrlPayloadAction,
    SetIsEmailTemplateOpenAction,
    SaveEmailTemplateSuccessAction,
    ModifyEmailTemplateAction,
    TemplateSetFromGalleryAction,
    TemplateSetMultiSignUpEditMessageAction,
    UpdateTemplatesSaveSettingsAction
} from './types';

const generateName = (name?: string): string =>
    `${name ?? 'Checkout template form'}: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;

const getDefaultTemplateState = (): CheckoutCreatorTemplate => ({
    status: EditorTemplateStatus.DEFAULT,
    isNew: true,
    files: {
        filesList: [],
        isFileUploading: false,
    },
    autosaveCounter: 0,
    recentColors: [],
    forms: [],
    isDoubleOptInDisabledByDefault: true,
});

const getDefaultTemplateFormPayload = (blockClientId: string, isDoubleOptInDisabled: boolean, formPayload?: Partial<FormPayload>): Partial<FormPayload> => {
    const payloadFormSettings = formPayload?.FormSettings || [];
    return {
        Name: generateName(blockClientId),
        Html: '',
        Css: '',
        FormType: FormTypeEnum.LandingPageForm,
        FormTemplateName: null,
        ...formPayload,
        FormSettings: isDoubleOptInDisabled ? [FormSettingsEnum.DoubleOptInDisabled, ...payloadFormSettings] : [FormSettingsEnum.AddDoubleOptIn, ...payloadFormSettings]
    };
};

const defaultState: CheckoutCreatorState = {
    stepInEdit: null,
    status: CheckoutCreatorStatus.DEFAULT,
    isTemplateOpen: false,
    isEmailTemplateOpen: false,
    isTemplateDeselected: false,
    checkoutObject: {
        Currency: LandingProductCurrencyEnum.USD
    },
    checkoutPayload: null,
    template: getDefaultTemplateState(),
    templateReplaced: null,
    openUpsellingFeature: null,
    templatePreview: {
        content: null,
        name: null,
        isPublic: null,
    },
    isGalleryOpen: false,
    isAddListError: false,
    templatesSaveSettings: null
};

const loadStart = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.LOADING
});

const loadSuccess = (state: CheckoutCreatorState, action: LoadSuccessAction): CheckoutCreatorState => {
    const { checkoutObject } = action.payload;

    const checkoutMetaDataObject: CheckoutMetaData | null = checkoutObject?.ProductLanding?.LandingMetaJson
        ? JSON.parse(Buffer.from(checkoutObject?.ProductLanding?.LandingMetaJson, 'base64').toString('utf8'))
        : null;
    

    const { ProductLanding: product, CheckoutLanding: payment, PaymentConfirmationLanding: confirmation } = checkoutObject ?? {};

    const productEditorMetaData = product?.Template?.EditorMetaData ? JSON.parse(Buffer.from(product?.Template?.EditorMetaData , 'base64').toString('utf8')) : null;
    const paymentEditorMetaData = payment?.Template?.EditorMetaData ? JSON.parse(Buffer.from(payment?.Template?.EditorMetaData , 'base64').toString('utf8')) : null;
    const confirmationEditorMetaData = confirmation?.Template?.EditorMetaData ? JSON.parse(Buffer.from(confirmation?.Template?.EditorMetaData , 'base64').toString('utf8')) : null;
    
    const emailLoadedJson = checkoutObject?.PostPurchaseTemplate?.EditorMetaData ? JSON.parse(Buffer.from(checkoutObject?.PostPurchaseTemplate?.EditorMetaData, 'base64').toString('utf8')) : null;

    return {
        ...state,
        status: CheckoutCreatorStatus.LOADED,
        checkoutObject,
        checkoutMetaDataObject,

        template: {
            ...state.template,
            loadedJson: productEditorMetaData?.data?.contentJson,
            paymentLoadedJson: paymentEditorMetaData?.data?.contentJson,
            confirmationLoadedJson: confirmationEditorMetaData?.data?.contentJson,
            emailLoadedJson: emailLoadedJson?.data?.contentJson,
            isNew: true,
            status: EditorTemplateStatus.LOADED,
        },
    };
}

const loadError = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.LOAD_ERROR
});

const saveStart = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.SAVING,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVING
    }
});

const saveSuccess = (state: CheckoutCreatorState, action: SaveSuccessAction): CheckoutCreatorState => ({
    ...state,
    stepInEdit: null,
    status: CheckoutCreatorStatus.SAVED,
    checkoutObject: action.payload.checkoutObject,
    checkoutPayload: null,
    urlPayload: null,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVED,
        isNew: false,
        loadedJson: action.payload?.editorSaveData?.multiTemplates?.[PRODUCT_MULTI_TEMPLATE_INDEX]?.contentJson ?? state.template.loadedJson, 
        paymentLoadedJson: action.payload?.editorSaveData?.multiTemplates?.[PAYMENT_MULTI_TEMPLATE_INDEX]?.contentJson ?? state.template.paymentLoadedJson, 
        confirmationLoadedJson: action.payload?.editorSaveData?.multiTemplates?.[CONFIRMATION_MULTI_TEMPLATE_INDEX]?.contentJson ?? state.template.confirmationLoadedJson, 
        recentColors: action.payload?.editorSaveData?.recentColors ?? state.template.recentColors,
        formsFromReplacedTemplate: null,
        emailLoadedJson: state.template.emailLoadedJson ?? JSON.parse(Buffer.from(action.payload.checkoutObject?.PostPurchaseTemplate?.EditorMetaData, 'base64').toString('utf8'))?.data?.contentJson
    },
    templateReplaced: null,
    checkoutMetaDataPayload: null,
    checkoutMetaDataObject: action.payload.checkoutMetaDataObject ?? state.checkoutMetaDataPayload,
    isTemplateDeselected: false,
});

const saveError = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.SAVE_ERROR,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVE_ERROR
    }
});


const saveEmailTemplateStart = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVING
    }
});

const saveEmailTemplateSuccess = (state: CheckoutCreatorState, action: SaveEmailTemplateSuccessAction): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVED,
        isNew: false,
        emailLoadedJson: action.payload?.editorSaveData?.contentJson ?? state.template.emailLoadedJson, 
        recentColors: action.payload?.editorSaveData?.recentColors ?? state.template.recentColors,
    },
    checkoutObject: {
        ...state.checkoutObject,
        PostPurchaseTemplate: action.payload.savedEmailTemplate ?? state.checkoutObject.PostPurchaseTemplate
    }
});

const saveEmailTemplateError = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVE_ERROR
    }
});


const modifyCheckout = (state: CheckoutCreatorState, action: ModifyCheckoutAction): CheckoutCreatorState => ({
    ...state,
    checkoutPayload: {
        ...state.checkoutPayload,
        ...action.payload.checkoutPayload
    }
});

const modifyCheckoutMetadata = (state: CheckoutCreatorState, action: ModifyCheckoutMetaDataAction): CheckoutCreatorState => ({
    ...state,
    checkoutMetaDataPayload: {
        ...state?.checkoutMetaDataPayload,
        data: {
            ...state.checkoutMetaDataPayload?.data,
            socialPreview: {
                ...state.checkoutMetaDataPayload?.data?.socialPreview,
                ...action.payload.checkoutMetaDataPayload?.data?.socialPreview
            }
        }
    }
});

const startEditStep = (state: CheckoutCreatorState, action: StartEditStepAction): CheckoutCreatorState => ({
    ...state,
    stepInEdit: action.payload.stepInEdit,
    checkoutPayload: null,
    checkoutMetaDataPayload: null,
    isTemplateDeselected: false,
    template: {
        ...state.template,
        ...state.templateReplaced,
        forms: state.isTemplateDeselected ? state.template.formsFromReplacedTemplate : state.template.forms,
        formsFromReplacedTemplate: null
    },
});

const cancelEditStep = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    checkoutPayload: null,
    stepInEdit: null,
    isTemplateDeselected: false,
    template: {
        ...state.template,
        ...state.templateReplaced,
        forms: state.isTemplateDeselected ? state.template.formsFromReplacedTemplate : state.template.forms,
        formsFromReplacedTemplate: null
    },
    checkoutMetaDataPayload: null,
    urlPayload: null,
    postPurchaseTemplatePayload: null,
    errors: null
});

const templateUploadFileStart = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: true,
        }
    }
});

const templateUploadFileSuccess = (
    state: CheckoutCreatorState,
    action: TemplateUploadFileSuccessAction
): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            filesList: action.payload.filesList,
            isFileUploading: false,
        }
    },
});

const templateUploadFileError = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: false,
        },
    }
});

const templateIncrementAutosaveCounter = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        autosaveCounter: 1 + state.template.autosaveCounter,
    }
});

const templateFormAddNew = (state: CheckoutCreatorState, action: TemplateFormAddNewAction): CheckoutCreatorState => {
    const { blockClientId } = action.payload;
    const exsitngTemplateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (exsitngTemplateIndex > -1) {
        const newTemplateForm = cloneDeep(state.template.forms[exsitngTemplateIndex]);
        newTemplateForm.isDeleted = false;
        const newTemplateForms = [...state.template.forms];
        newTemplateForms.splice(exsitngTemplateIndex, 1, newTemplateForm);
        return {
            ...state,
            template: {
                ...state.template,
                forms: newTemplateForms
            }
        };
    }
    return {
        ...state,
        template: {
            ...state.template,
            forms: [...state.template.forms ?? [], {
                blockClientId,
                isError: false,
                isSaved: false,
                isDeleted: false,
                formPayload: getDefaultTemplateFormPayload(action.payload.blockClientId, state.template.isDoubleOptInDisabledByDefault)
            }]
        }
    };
};

const templateFormDuplicated = (state: CheckoutCreatorState, action: TemplateFormDuplicatedAction): CheckoutCreatorState => {
    const { blockClientId, newBlockClientId } = action.payload;
    const templateToDuplicate = state.template.forms?.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToDuplicate) {
        return state;
    }
    const newFormName = generateName(newBlockClientId);
    const templateForm = cloneDeep(templateToDuplicate);
    templateForm.blockClientId = newBlockClientId;
    templateForm.formPayload.Name = newFormName
    templateForm.form = null;
    return {
        ...state,
        template: {
            ...state.template,
            forms: [
                ...state.template.forms,
                templateForm
            ]
        }
    };
};

const templateFormPayloadUpdate = (state: CheckoutCreatorState, action: TemplateFormPayloadUpdateAction): CheckoutCreatorState => {
    const { blockClientId, formPayload } = action.payload;
    const templateToUpdateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (templateToUpdateIndex === -1) {
        return {
            ...state,
            template: {
                ...state.template,
                forms: [
                    ...state.template.forms,
                    {
                        blockClientId,
                        form: null,
                        formPayload: getDefaultTemplateFormPayload(blockClientId, state.template.isDoubleOptInDisabledByDefault, formPayload),
                        isError: false,
                        isSaved: false,
                        isDeleted: false,
                    }
                ]
            }
        };
    }
    const newTemplateForm = cloneDeep(state.template.forms[templateToUpdateIndex]);
    newTemplateForm.isDeleted = false;
    newTemplateForm.formPayload = { ...newTemplateForm.formPayload, ...formPayload };
    const newTemplateForms = [...state.template.forms];
    newTemplateForms.splice(templateToUpdateIndex, 1, newTemplateForm)
    return {
        ...state,
        template: {
            ...state.template,
            forms: newTemplateForms
        }
    };
};

const templateFormSetMailingPreferencesLabel = (state: CheckoutCreatorState, action: TemplateFormSetMailingPreferencesLabelAction): CheckoutCreatorState => {
    const { blockClientId, formMailingPreferencesLabel } = action.payload;
    const templateToUpdateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (templateToUpdateIndex === -1) {//won't happen – just a safeguard
        return state;
    }
    const newTemplateForm = cloneDeep(state.template.forms[templateToUpdateIndex]);
    newTemplateForm.isDeleted = false;
    newTemplateForm.formMailingPreferencesLabel = formMailingPreferencesLabel;
    const newTemplateForms = [...state.template.forms];
    newTemplateForms.splice(templateToUpdateIndex, 1, newTemplateForm)
    return {
        ...state,
        template: {
            ...state.template,
            forms: newTemplateForms
        }
    };
};

const templateFormsRemove = (state: CheckoutCreatorState, action: TemplateFormsRemoveAction): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        forms: state.template?.forms?.map((templateForm) => {
            if (action.payload.formsBlockClientIds.includes(templateForm.blockClientId)) {
                return {
                    ...templateForm, 
                    isDeleted: true,
                    form: null
                }
            }
            return templateForm;
        })
    }
});

const TemplateFormsFromReplacedTemplateClear = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        formsFromReplacedTemplate: null
    }
});

const templateFormsSetInvalid = (state: CheckoutCreatorState, action: TemplateFormsSetInvalidAction): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        forms: state.template?.forms?.map((templateForm) => {
            const isError = action.payload.blockClientIds.includes(templateForm.blockClientId);
            const isFirstTimeError = isNil(templateForm.isFirstTimeError) ? true : false;
            return (
            {
                ...templateForm,
                isFirstTimeError: isError ? isFirstTimeError : null,
                isError,
            }
        )})
    }
});

const TemplateFormsSetDefaultDoubleOptInDisabled = (state: CheckoutCreatorState, action: TemplateFormsSetDefaultDoubleOptInDisabledAction): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        isDoubleOptInDisabledByDefault: action.payload.isDoubleOptInDisabledByDefault
    }
});

const templateFormSetErrors = (state: CheckoutCreatorState, action: TemplateFormSetErrorsAction): CheckoutCreatorState => {
    const templateForms = cloneDeep(state.template.forms);
    const { blockClientId, errors } = action.payload;
    const templateToUpdate = templateForms.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToUpdate) {
        return state;
    }
    templateToUpdate.errors = errors;
    return {
        ...state,
        template: {
            ...state.template,
            forms: templateForms
        }
    };
};

const templateFormUpdateSaved = (state: CheckoutCreatorState, action: TemplateFormUpdateSavedAction): CheckoutCreatorState => {
    const templateForms = cloneDeep(state.template.forms);
    const { blockClientId, form } = action.payload;
    const templateToUpdate = templateForms.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToUpdate) {
        return state;
    }
    templateToUpdate.form = form;
    templateToUpdate.isFirstTimeError = null;
    return {
        ...state,
        template: {
            ...state.template,
            forms: templateForms
        }
    };
};

const templateFormAddLoaded = (state: CheckoutCreatorState, action: TemplateFormAddLoadedAction): CheckoutCreatorState => {
    const { blockClientId, form, formMailingPreferencesLabel } = action.payload;
    const {
        Name,
        Html,
        Css,
        FormType,
        FormTemplateName,
        Audience,
        MergeFields,
        FormScope: Scope,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
        EmailsToNotifyOnAction
    } = form;

    const formPayload: FormPayload = {
        Name,
        Html,
        Css,
        Scope,
        FormType: FormType ?? FormTypeEnum.FormCreator,
        FormTemplateName,
        Audience: Audience?.map((item) => item.PublicListID),
        MergeFields,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
        EmailsToNotifyOnAction,
    };

    return {
        ...state,
        template: {
            ...state.template,
            forms: [...state.template.forms ?? [], {
                blockClientId,
                isError: false,
                isSaved: true,
                isDeleted: false,
                form,
                formPayload,
                formMailingPreferencesLabel
            }]
        }
    };
};

const templateChanged = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.MODIFIED,
    }
});

const templateDeselect = (state: CheckoutCreatorState): CheckoutCreatorState => ({
        ...state,
        isTemplateDeselected: true,
        stepInEdit: 2,
    }
);

const setIsTemplateOpen = (state: CheckoutCreatorState, action: SetIsTemplateOpenAction): CheckoutCreatorState => {
    if (action.payload.isOpen) {
        return {
            ...state,
            isTemplateOpen: true
        }
    }
    
    return {
        ...state,
        isTemplateOpen: false,
        template: state.templateReplaced ?? state.template,
        templateReplaced: null
    };
}

const setIsEmailTemplateOpen = (state: CheckoutCreatorState, action: SetIsEmailTemplateOpenAction): CheckoutCreatorState => ({
        ...state,
        isEmailTemplateOpen: action.payload.isOpen,
    }
);

const openUpsellingOverlay = (state: CheckoutCreatorState, action: OpenUpsellingOverlayAction): CheckoutCreatorState => ({
    ...state,
    openUpsellingFeature: action.payload.feature,
});

const setTemplatePreview = (state: CheckoutCreatorState, action: SetTemplatePreviewAction): CheckoutCreatorState => ({
    ...state,
    templatePreview: {
        content: action.payload.content,
        name: action.payload.name,
        isPublic: action.payload.isPublic,
    },
});

const templateSetMultiSignUpEditMessage = (state: CheckoutCreatorState, action: TemplateSetMultiSignUpEditMessageAction): CheckoutCreatorState => ({
    ...state,
    template: {
        ...state.template,
        multiSignUpEditMessage: action.payload.editMessage
    }
});

const closeUpsellingOverlay = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    openUpsellingFeature: null,
});

const openGallery = (state: CheckoutCreatorState): CheckoutCreatorState => ({
        ...state,
        isGalleryOpen: true,
    }
);

const closeGallery = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    isGalleryOpen: false,
});

const modifyEmailTemplate = (state: CheckoutCreatorState, action: ModifyEmailTemplateAction): CheckoutCreatorState => ({
    ...state,
    postPurchaseTemplatePayload: {
        ...state.postPurchaseTemplatePayload,
        ...action.payload
    }
});

const setCheckoutErrors = (state: CheckoutCreatorState, action: SetCheckoutErrorsAction): CheckoutCreatorState => ({
    ...state,
    errors: action.payload.errors,
    status: CheckoutCreatorStatus.SAVE_ERROR
});

const addContactListErrorStatusChange = (state: CheckoutCreatorState, action: AddContactListErrorStatusAction): CheckoutCreatorState => ({
    ...state,
    isAddListError: action.payload.isAddListError
});


const setUrlPayload = (state: CheckoutCreatorState, action: SetUrlPayloadAction): CheckoutCreatorState => ({
    ...state,
    urlPayload: action.payload.urlSuffix
});

const updateTemplatesSaveSettings = (state: CheckoutCreatorState, action: UpdateTemplatesSaveSettingsAction): CheckoutCreatorState => ({
    ...state,
    templatesSaveSettings: {
        ...state.templatesSaveSettings,
        ...action.payload
    }
});

const pageTemplateSuccess = (state: CheckoutCreatorState, action: LoadPageTemplateSuccessAction): CheckoutCreatorState => {
    const { pageTemplateType, pageTemplate } = action.payload;
    const newParseTemplates = {
        ...(pageTemplateType === PreviewPageType.ActivationViaEmail && {activationViaEmailPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.AlreadySubscribed && {alreadySubscribedPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.FormSubmit && {formSubmitPage: pageTemplate}),
    }

    return {
        ...state,
        pageTemplates: {
            ...state.pageTemplates,
            ...newParseTemplates
        }
    }
};

const templateSetFromGallery = (state: CheckoutCreatorState, action: TemplateSetFromGalleryAction): CheckoutCreatorState => {
    const { productTemplate, paymentTemplate, confirmationTemplate } = action.payload;
    const productEditorMetaData = productTemplate?.EditorMetaData ? JSON.parse(Buffer.from(productTemplate?.EditorMetaData , 'base64').toString('utf8')) : null;
    const paymentEditorMetaData = paymentTemplate?.EditorMetaData ? JSON.parse(Buffer.from(paymentTemplate?.EditorMetaData , 'base64').toString('utf8')) : null;
    const confirmationEditorMetaData = confirmationTemplate?.EditorMetaData ? JSON.parse(Buffer.from(confirmationTemplate?.EditorMetaData , 'base64').toString('utf8')) : null;
    
    // If templates aren't loaded just hide gallery, it should be an edge case but it's better to not show the designer at all in such case
    if (!productEditorMetaData?.data?.contentJson || !paymentEditorMetaData?.data?.contentJson || !confirmationEditorMetaData?.data?.contentJson) {
        return {
            ...state,
            isGalleryOpen: false
        }
    }

    return {
        ...state,
        templateReplaced: state.template,
        template: {
            ...state.template,
            loadedJson: productEditorMetaData?.data?.contentJson,
            paymentLoadedJson: paymentEditorMetaData?.data?.contentJson,
            confirmationLoadedJson: confirmationEditorMetaData?.data?.contentJson,
            isNew: true,
            status: EditorTemplateStatus.LOADED,
        },
        isTemplateOpen: true,
        isGalleryOpen: false
    }
};

const publishCheckoutStart = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.SAVING,
});

const publishCheckoutError = (state: CheckoutCreatorState): CheckoutCreatorState => ({
    ...state,
    status: CheckoutCreatorStatus.SAVE_ERROR,
});

const reducer: Reducer<CheckoutCreatorState> = (
    state: CheckoutCreatorState = defaultState,
    action
): CheckoutCreatorState => {
    switch (action.type) { // eslint-disable-line sonarjs/max-switch-cases
        case CheckoutCreatorActionTypes.LOAD_START:
            return loadStart(state);
        case CheckoutCreatorActionTypes.LOAD_SUCCESS:
            return loadSuccess(state, action as LoadSuccessAction);
        case CheckoutCreatorActionTypes.LOAD_ERROR:
            return loadError(state);
        case CheckoutCreatorActionTypes.SAVE_START:
            return saveStart(state);
        case CheckoutCreatorActionTypes.SAVE_SUCCESS:
            return saveSuccess(state, action as SaveSuccessAction);
        case CheckoutCreatorActionTypes.SAVE_ERROR:
            return saveError(state);
        case CheckoutCreatorActionTypes.SAVE_EMAIL_TEMPLATE_START:
            return saveEmailTemplateStart(state);
        case CheckoutCreatorActionTypes.SAVE_EMAIL_TEMPLATE_SUCCESS:
            return saveEmailTemplateSuccess(state, action as SaveEmailTemplateSuccessAction);
        case CheckoutCreatorActionTypes.SAVE_EMAIL_TEMPLATE_ERROR:
            return saveEmailTemplateError(state);
        case CheckoutCreatorActionTypes.MODIFY_CHECKOUT:
            return modifyCheckout(state, action as ModifyCheckoutAction);
        case CheckoutCreatorActionTypes.MODIFY_CHECKOUT_METADATA:
            return modifyCheckoutMetadata(state, action as ModifyCheckoutMetaDataAction);
        case CheckoutCreatorActionTypes.START_EDIT_STEP:
            return startEditStep(state, action as StartEditStepAction);
        case CheckoutCreatorActionTypes.CANCEL_EDIT_STEP:
            return cancelEditStep(state);
        case CheckoutCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START:
            return templateUploadFileStart(state);
        case CheckoutCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS:
            return templateUploadFileSuccess(state, action as TemplateUploadFileSuccessAction);
        case CheckoutCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR:
            return templateUploadFileError(state);
        case CheckoutCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER:
            return templateIncrementAutosaveCounter(state);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_ADD_NEW:
            return templateFormAddNew(state, action as TemplateFormAddNewAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORMS_REMOVE:
            return templateFormsRemove(state, action as TemplateFormsRemoveAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR:
            return TemplateFormsFromReplacedTemplateClear(state);
        case CheckoutCreatorActionTypes.TEMPLATE_FORMS_SET_INVALID:
            return templateFormsSetInvalid(state, action as TemplateFormsSetInvalidAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN:
            return TemplateFormsSetDefaultDoubleOptInDisabled(state, action as TemplateFormsSetDefaultDoubleOptInDisabledAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_SET_ERRORS:
            return templateFormSetErrors(state, action as TemplateFormSetErrorsAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_UPDATE_SAVED:
            return templateFormUpdateSaved(state, action as TemplateFormUpdateSavedAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_ADD_LOADED:
            return templateFormAddLoaded(state, action as TemplateFormAddLoadedAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL:
            return templateFormSetMailingPreferencesLabel(state, action as TemplateFormSetMailingPreferencesLabelAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_DUPLICATED:
            return templateFormDuplicated(state, action as TemplateFormDuplicatedAction);
        case CheckoutCreatorActionTypes.TEMPLATE_FORM_PAYLOAD_UPDATE:
            return templateFormPayloadUpdate(state, action as TemplateFormPayloadUpdateAction);
        case CheckoutCreatorActionTypes.MODIFY_EMAIL_TEMPLATE:
            return modifyEmailTemplate(state, action as ModifyEmailTemplateAction);
        case CheckoutCreatorActionTypes.TEMPLATE_CHANGED:
            return templateChanged(state);
        case CheckoutCreatorActionTypes.TEMPLATE_DESELECT:
            return templateDeselect(state);
        case CheckoutCreatorActionTypes.SET_IS_TEMPLATE_OPEN:
            return setIsTemplateOpen(state, action as SetIsTemplateOpenAction);
        case CheckoutCreatorActionTypes.SET_IS_EMAIL_TEMPLATE_OPEN:
            return setIsEmailTemplateOpen(state, action as SetIsEmailTemplateOpenAction);
        case CheckoutCreatorActionTypes.SET_TEMPLATE_PREVIEW:
            return setTemplatePreview(state, action as SetTemplatePreviewAction);
        case CheckoutCreatorActionTypes.TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE:
            return templateSetMultiSignUpEditMessage(state, action as TemplateSetMultiSignUpEditMessageAction);
        case CheckoutCreatorActionTypes.OPEN_UPSELLING_OVERLAY:
            return openUpsellingOverlay(state, action as OpenUpsellingOverlayAction);
        case CheckoutCreatorActionTypes.CLOSE_UPSELLING_OVERLAY:
            return closeUpsellingOverlay(state);
        case CheckoutCreatorActionTypes.OPEN_GALLERY:
            return openGallery(state);
        case CheckoutCreatorActionTypes.CLOSE_GALLERY:
            return closeGallery(state);
        case CheckoutCreatorActionTypes.SET_CHECKOUT_ERRORS:
            return setCheckoutErrors(state, action as SetCheckoutErrorsAction);
        case CheckoutCreatorActionTypes.SET_URL_PAYLOAD:
            return setUrlPayload(state, action as SetUrlPayloadAction);
        case CheckoutCreatorActionTypes.PUBLISH_CHECKOUT_START:
            return publishCheckoutStart(state);
        case CheckoutCreatorActionTypes.PUBLISH_CHECKOUT_ERROR:
            return publishCheckoutError(state);
        case CheckoutCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS:
            return addContactListErrorStatusChange(state, action as AddContactListErrorStatusAction);
        case CheckoutCreatorActionTypes.TEMPLATE_SET_FROM_GALLERY:
            return templateSetFromGallery(state, action as TemplateSetFromGalleryAction);
        case CheckoutCreatorActionTypes.UPDATE_TEMPLATES_SAVE_SETTINGS:
            return updateTemplatesSaveSettings(state, action as UpdateTemplatesSaveSettingsAction);
        case CheckoutCreatorActionTypes.RESET:
            return defaultState;
        case CheckoutCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS:
            return pageTemplateSuccess(state, action as LoadPageTemplateSuccessAction);
        default:
            return state;
    }
};

export { reducer as checkoutCreator };

export default reducer;
